import React, { createContext, useContext } from "react";

/**
 * Hook that the Pages renderer reaches into to find out whether the
 * surrounding host wants the "last page" CTA to submit (preview /
 * editor canvas) or behave as a step navigator only (fill flow).
 *
 * Moved out of apps/web so the Pages renderer can stay in the
 * shared package — the editor still wraps its preview in
 * `<PreviewFormProvider>` to plug its own submit handler in.
 */
interface PreviewFormContextValue {
  /**
   * Called by the Pages element's last-page button so the visual
   * Submit affordance lives with Pages while the submit lifecycle
   * stays owned by whoever wraps the preview (e.g. PreviewContent).
   * Undefined in non-submitting contexts.
   */
  onSubmit?: () => void | Promise<void>;
  /** True while the (mock) submit handler is in-flight. */
  submitting?: boolean;
  /** Custom label for the submit CTA (falls back to "Submit"). */
  submitLabel?: string;
  /**
   * Host-owned status widget (e.g. the autosave "All changes saved"
   * indicator). The desktop fill chrome places it in the answer-column
   * header; hosts stay the source of the widget so it keeps host-specific
   * data/behaviour.
   */
  statusSlot?: React.ReactNode;
  /**
   * Host-owned Submit control, used in place of the built-in Submit button on
   * the final step. Lets the public filler own the whole submit affordance —
   * e.g. the Cloudflare Turnstile popover that floats above the button on
   * challenge — while the engine keeps positioning it in the footer. When
   * absent (preview / editor canvas), the engine renders its default Submit.
   */
  submitSlot?: React.ReactNode;
}

const PreviewFormContext = createContext<PreviewFormContextValue | null>(null);

export const PreviewFormProvider: React.FC<{
  onSubmit?: () => void | Promise<void>;
  submitting?: boolean;
  submitLabel?: string;
  statusSlot?: React.ReactNode;
  submitSlot?: React.ReactNode;
  children: React.ReactNode;
}> = ({
  onSubmit,
  submitting,
  submitLabel,
  statusSlot,
  submitSlot,
  children,
}) => (
  <PreviewFormContext.Provider
    value={{ onSubmit, submitting, submitLabel, statusSlot, submitSlot }}
  >
    {children}
  </PreviewFormContext.Provider>
);

export const usePreviewFormContext = () => useContext(PreviewFormContext);
